import { useState, useEffect, useRef } from "react";

const C = {
  bg: "#080A0E", card: "#0D1117", border: "#1C2333",
  gold: "#C9A84C", goldLight: "#E8C96A", goldFaint: "#C9A84C18", goldGlow: "#C9A84C30",
  cyan: "#00D4FF", cyanFaint: "#00D4FF15",
  green: "#00E676", greenFaint: "#00E67615",
  red: "#FF4444", redFaint: "#FF444415",
  orange: "#FF8C00", orangeFaint: "#FF8C0015",
  purple: "#9B59B6", purpleFaint: "#9B59B615",
  white: "#F0F4FF", gray: "#8892A4", grayLight: "#B0BBCC", muted: "#4A5568",
};

const nextgenChallenges = [
  {
    name: "STANDARD", tag: "RECOMMENDED FOR FLEXX STUDENTS", tagColor: C.green,
    phases: "2-Phase", target: "10% → 5%", maxDD: "10%", dailyDD: "5%",
    minDays: "3 days", leverage: "100×", newsTrading: true, easAllowed: true, weekendHold: true, split: "90%",
    fees: { "5K":"$41","10K":"$90","25K":"$179","50K":"$329","100K":"$529","200K":"$1,069" },
    note: "Best for Flexx students. Unlimited time, most forgiving drawdown rules. Balance-based DD. Start here.",
  },
  {
    name: "RAPID", tag: "SPEED RUN — NO MIN DAYS", tagColor: C.cyan,
    phases: "2-Phase", target: "8% → 5%", maxDD: "8%", dailyDD: "5%",
    minDays: "0 days", leverage: "100×", newsTrading: true, easAllowed: true, weekendHold: true, split: "90%",
    fees: { "5K":"$41","10K":"$90","25K":"$179","50K":"$329","100K":"$529","200K":"$1,069" },
    note: "No minimum trading days — pass as fast as your system allows. Tighter 8% max DD.",
  },
  {
    name: "ACE", tag: "SINGLE PHASE — ADVANCED ONLY", tagColor: C.gold,
    phases: "1-Phase", target: "10%", maxDD: "6%", dailyDD: "3%",
    minDays: "3 days", leverage: "50×", newsTrading: false, easAllowed: true, weekendHold: true, split: "90%",
    fees: { "5K":"$46","10K":"$100","25K":"$204","50K":"$379","100K":"$629","200K":"$1,269" },
    note: "One phase only — but very strict. 3% daily DD, no news trading, 50× max leverage. Experienced traders only.",
  },
];

const scalingPlan = [
  { months:"0 months", balance:"$200,000", daily:"$10,000", max:"$20,000" },
  { months:"4 months", balance:"$250,000", daily:"$12,500", max:"$25,000" },
  { months:"8 months", balance:"$300,000", daily:"$15,000", max:"$30,000" },
  { months:"12 months", balance:"$350,000", daily:"$17,500", max:"$35,000" },
  { months:"16 months", balance:"$400,000", daily:"$20,000", max:"$40,000" },
];

const ngRules = [
  { rule:"5% Daily Drawdown", severity:"KILL SWITCH", color:C.red, desc:"Lose 5% of your balance in a single day → account closed instantly. Structure every trade so one bad day cannot breach this." },
  { rule:"10% Max Drawdown (Standard)", severity:"ACCOUNT BREACH", color:C.red, desc:"Total losses exceed 10% of starting balance → failed. Balance-based (not equity) — floating trades don't count until closed." },
  { rule:"Minimum 3 Trading Days", severity:"REQUIREMENT", color:C.orange, desc:"Standard & Ace: must trade at least 3 separate calendar days before hitting the profit target and requesting evaluation." },
  { rule:"EAs Fully Allowed", severity:"GREEN LIGHT", color:C.green, desc:"Run your MT5 EA 24/7 on the challenge account. Weekend holding allowed. This directly fits your Month 2 EA build." },
  { rule:"News Trading Allowed (Standard/Rapid)", severity:"GREEN LIGHT", color:C.green, desc:"Trade NFP, CPI, FOMC with no restrictions on Standard and Rapid. Ace challenge bans news trading — avoid Ace if you trade news." },
  { rule:"Fee Fully Refunded", severity:"BONUS", color:C.cyan, desc:"Challenge fee is reimbursed with your first funded payout. Net cost to become a funded trader = $0 if you pass." },
];

const roadmap = [
  {
    month:"MONTH 1", subtitle:"Foundation Mastery", weeks:"Weeks 1–4",
    color:C.cyan, faint:C.cyanFaint, icon:"◈",
    focus:"Build the unbreakable base. 80% of your future success is decided here.",
    pareto:[
      "Market Structure (BOS/CHoCH) — the language of ALL professional analysis",
      "Risk Management — 1% rule, position sizing, stop placement",
      "Multi-Timeframe Analysis (HTF → LTF) — seeing the full picture",
      "Session Timing — London, NY, Tokyo — when to trade and when to STOP",
    ],
    skills:[
      {
        week:"W1–2", title:"Market Structure & Price Action", tag:"CRITICAL 20%", tagColor:C.cyan,
        items:[
          "Break of Structure (BOS) — trend continuation confirmation",
          "Change of Character (CHoCH) — reversal identification",
          "Swing Highs / Swing Lows — the skeleton of every chart",
          "Higher Highs / Lower Lows — never fade the trend",
          "Support & Resistance levels — where price respects",
        ],
        tools:["TradingView (chart analysis)","ICT YouTube / Smart Money Concepts"],
        milestone:"Correctly identify BOS/CHoCH on 10 different historical charts across EUR/USD, BTC/USD, and NAS100 without assistance.",
      },
      {
        week:"W2–3", title:"Risk Management — Foundation of Longevity", tag:"NON-NEGOTIABLE", tagColor:C.red,
        items:[
          "1% Rule per trade — never exceed, ever",
          "Risk:Reward minimum 1:2 — every single trade",
          "Position Size Formula: (Account × 1%) ÷ (Stop Pips × Pip Value)",
          "Forex: MyFXBook calculator every trade",
          "Crypto: Never exceed 2–5× leverage",
          "Indices: 0.5% risk until 3 months profitable",
          "Max 3 concurrent trades — 3% total exposure",
        ],
        tools:["MyFXBook Position Size Calculator","Excel/Sheets trade journal"],
        milestone:"100 demo trades with perfect position sizing. Zero over-sized entries. Track every one.",
      },
      {
        week:"W3–4", title:"Multi-Timeframe Analysis (MTF)", tag:"CORE 20%", tagColor:C.cyan,
        items:[
          "Top-Down Analysis: Monthly → Weekly → Daily → 4H → 1H",
          "HTF sets the BIAS (trend direction) — never fight it",
          "LTF sets the ENTRY (precision timing)",
          "Confluence = when multiple timeframes agree on the same level",
          "Forex: Daily/4H bias, enter on 1H/15M",
          "Crypto: Weekly/Daily bias, enter on 4H/1H",
          "Indices: Daily bias, enter on 1H/15M",
        ],
        tools:["TradingView multi-layout view"],
        milestone:"Build a trade plan on 5 different setups showing HTF bias + LTF entry on all 3 markets.",
      },
    ],
    daily:[
      {time:"15 min",task:"Pre-market session prep: HTF bias check on 3 markets"},
      {time:"20 min",task:"Chart study: Mark structure, key levels, POIs on demo"},
      {time:"15 min",task:"Journal review: Grade yesterday's trades, note patterns"},
      {time:"10 min",task:"Watch 1 ICT/SMC educational clip relevant to current lesson"},
    ],
    checkpoint:"End of Month 1: Execute 50 demo trades with documented analysis. Win rate not the goal — PROCESS is. Can you explain every entry and exit?",
  },
  {
    month:"MONTH 2", subtitle:"System & Strategy", weeks:"Weeks 5–8",
    color:C.gold, faint:C.goldFaint, icon:"◆",
    focus:"Stack your edge. Build the repeatable system that generates consistent results.",
    pareto:[
      "Order Blocks & Fair Value Gaps — institutional entry zones",
      "Liquidity Concepts — where stop hunts happen and why",
      "Economic Calendar mastery — trading around high-impact news",
      "EA/Algorithmic basics — automating your manual system",
    ],
    skills:[
      {
        week:"W5–6", title:"Institutional Concepts (ICT/SMC)", tag:"CORE 20%", tagColor:C.gold,
        items:[
          "Order Blocks (OB): Last bullish/bearish candle before a significant move — institutions leave footprints",
          "Fair Value Gaps (FVG/Imbalance): Price voids that get filled — high-probability magnets",
          "Liquidity: BSL (Buy Side) and SSL (Sell Side) — where stops cluster",
          "Stop Hunts / Liquidity Grabs: Understand WHY price sweeps lows before reversing",
          "Optimal Trade Entry (OTE): 0.618–0.705 Fibonacci retracement zone",
          "Premium vs Discount: Buy in discount (below 50% of range), sell in premium",
          "Inducement: Price traps retail traders before reversing — spot it and don't fall for it",
        ],
        tools:["ICT Mentorship YouTube (free)","SMC Telegram communities"],
        milestone:"Mark OBs and FVGs on 20 historical setups. Back-test: how often did price return to these zones?",
      },
      {
        week:"W6–7", title:"News Trading & Fundamental Confluence", tag:"MACRO EDGE", tagColor:C.gold,
        items:[
          "ForexFactory: Filter HIGH impact news only (red folders)",
          "NFP, CPI, FOMC, GDP — the 4 pillars of forex movement",
          "Rule: NO new entries 30 mins before / after high-impact news",
          "Crypto: Fed decisions + BTC ETF flow data (Farside Investors)",
          "Indices: VIX (fear index) correlation — high VIX = sell pressure",
          "DXY (Dollar Index) correlation: DXY up = EUR/USD down, Gold down",
          "News as confirmation — not your reason to trade. Technical first.",
        ],
        tools:["ForexFactory Calendar","Farside BTC ETF Flows","TradingEconomics"],
        milestone:"Trade 10 news events on demo. Document: Was your technical setup confirmed? Did news MATCH or FIGHT your bias?",
      },
      {
        week:"W7–8", title:"EA, Automation & Algorithmic Trading Intro", tag:"MODERN EDGE", tagColor:C.purple,
        items:[
          "What is an EA (Expert Advisor): Automated MT4/MT5 strategy executor",
          "EAs don't create edge — they EXECUTE your edge consistently",
          "Start by automating what you ALREADY do profitably manually",
          "MQL5 community: Free and paid EAs — learn to evaluate them",
          "Backtesting: MT5 Strategy Tester — test on 10 years of data minimum",
          "Forward testing: Run EA on demo for 3 months before live",
          "Key EA parameters: Max lot size, risk%, drawdown limits, session filters",
          "AI-assisted EAs: ChatGPT / Claude to write basic MQL5 code",
        ],
        tools:["MT5 Strategy Tester","MQL5.com marketplace","ChatGPT (MQL5 code help)"],
        milestone:"Install 1 free EA on MT5 demo. Run 30-day forward test. Document performance vs manual trading in same period.",
      },
      {
        week:"W8", title:"Advanced AI Trading Tools", tag:"2026 EDGE", tagColor:C.cyan,
        items:[
          "TrendSpider: AI-powered automated technical analysis and pattern recognition",
          "Trade Ideas: AI stock/crypto scanner with real-time alerts",
          "ChatGPT / Claude: Strategy refinement, trade journaling, MQL5 code generation",
          "Sentiment Analysis: Fear & Greed Index, Twitter/X sentiment tools",
          "On-Chain Analytics (Crypto): Glassnode, CryptoQuant — smart money flow",
          "Quantitative signals: Unusual options flow (indices) via Unusual Whales",
        ],
        tools:["TrendSpider (trial)","Fear & Greed Index","Glassnode (crypto)","Unusual Whales (indices)"],
        milestone:"Build a trading dashboard combining: 1 AI scanner + economic calendar + sentiment indicator. Use it for daily session prep.",
      },
    ],
    daily:[
      {time:"10 min",task:"Check economic calendar for high-impact news + DXY direction"},
      {time:"25 min",task:"Top-down analysis + mark OBs, FVGs, liquidity zones"},
      {time:"20 min",task:"Execute 2–3 demo trades with full pre-trade checklist"},
      {time:"10 min",task:"Post-trade journal: Setup grade A/B/C, what worked, what didn't"},
      {time:"10 min",task:"Backtesting: Review 3–5 historical setups of the day's pattern"},
    ],
    checkpoint:"End of Month 2: 150 demo trades documented. Build a 'Best Setups' folder — 20 A-grade trade examples from your own analysis. These become your playbook.",
  },
  {
    month:"MONTH 3", subtitle:"Expert Execution", weeks:"Weeks 9–12",
    color:C.green, faint:C.greenFaint, icon:"◉",
    focus:"Graduate from student to funded trader. Quant thinking, EA deployment, and your NextGen Funding challenge path.",
    pareto:[
      "Trade Psychology & Discipline Systems — the final boss of trading",
      "Quant Trading basics — statistical edge, backtesting, data-driven decisions",
      "NextGen Funding challenge prep — know every rule before you risk the fee",
      "Portfolio-level thinking — correlation, diversification across markets",
    ],
    skills:[
      {
        week:"W9–10", title:"Quantitative Trading & Backtesting", tag:"EXPERT LEVEL", tagColor:C.green,
        items:[
          "What quant traders do: Find statistical edges via data, not gut feelings",
          "Expectancy Formula: (Win% × Avg Win) – (Loss% × Avg Loss) — must be positive",
          "Minimum 200 trade sample before judging any strategy",
          "Python basics for trading: pandas (data), matplotlib (charts), backtesting.py",
          "Free tools: QuantConnect, Backtrader, TradingView Pine Script",
          "Pine Script: Write your own indicators on TradingView (no prior coding needed)",
          "Key metrics: Sharpe Ratio, Max Drawdown, Win Rate, Profit Factor (target > 1.5)",
          "Correlation analysis: Trade markets that DON'T correlate — true diversification",
        ],
        tools:["QuantConnect (free)","TradingView Pine Script","Python + backtesting.py","ChatGPT for code"],
        milestone:"Build 1 custom Pine Script indicator. Backtest your primary strategy on MT5 across 5 years. Calculate: Win Rate, Profit Factor, Max Drawdown.",
      },
      {
        week:"W10–11", title:"Trade Psychology & Performance Systems", tag:"THE REAL EDGE", tagColor:C.green,
        items:[
          "The 3 killers: Revenge trading, FOMO entries, over-leveraging after losses",
          "Pre-trade checklist: Must complete before EVERY trade — no exceptions",
          "Daily max loss rule: Down 2%? Stop. Done for the day.",
          "Weekly loss cap: Down 5%? Take 3 days off. Reset.",
          "The 'Trader's State': Calm, detached, process-focused — not outcome-focused",
          "Mark Douglas 'Trading in the Zone' — the trading psychology bible",
          "Performance journaling: Trades + emotions + energy + mindset rating per session",
          "Review cadence: Daily (5 min), Weekly (30 min), Monthly (2 hours) — never skip",
        ],
        tools:["TraderVue or Edgewonk (trade journal)","Mark Douglas audiobook","Notion/Excel psychology tracker"],
        milestone:"30 consecutive days: follow pre-trade checklist + daily/weekly review. Zero revenge trades. Zero FOMO entries. Track compliance rate.",
      },
      {
        week:"W11", title:"NextGen Funding — Challenge Preparation", tag:"PROP FIRM PATH", tagColor:C.gold,
        items:[
          "Balance-based drawdown = floating losses don't count until trade is closed — more forgiving",
          "5% daily DD is your kill switch — risk only 0.5% per trade during challenge (not 1%)",
          "Standard 10% Phase 1 target = 1% gain/day for 10 good trading days. No rushing.",
          "Math: 0.5% risk + 1:2 R:R = only need 34% win rate to pass Phase 1 profitably",
          "Run a 30-day mock challenge on demo with exact NextGen rules BEFORE buying",
          "EAs fully allowed — deploy your Month 2 EA on the challenge account via VPS",
          "News trading allowed on Standard/Rapid — your ForexFactory discipline applies directly",
          "Weekend holding allowed — your swing trades don't need to close Friday",
          "Fee is refunded on first payout — net cost to get funded = $0 if you pass",
          "April 2026 promo: 30% OFF 50K/100K/200K with code PASS30 until 30 April",
        ],
        tools:["thenextgenfunding.com","MT5 (NextGen platform)","MyFXBook challenge tracker"],
        milestone:"Complete a 30-day mock challenge on demo following exact NextGen rules: 5% daily DD, 10% max DD, 3 min trading days. Pass it twice before buying the real challenge.",
      },
      {
        week:"W11–12", title:"Advanced EA & Algorithmic Deployment", tag:"AUTOMATION", tagColor:C.purple,
        items:[
          "Deploy your manually-tested strategy as a full EA on MT5",
          "Use ChatGPT/Claude to write and debug MQL5 code",
          "VPS hosting: Run EA 24/7 without your computer — ForexVPS, BeeksFX",
          "Multi-EA portfolio: 2–3 EAs on different pairs with combined risk management",
          "Monitoring: MyFXBook auto-sync — track all metrics in real-time",
          "Kill switch: Always have a manual override — markets can break any algorithm",
          "2026 trend: AI-enhanced EAs using GPT APIs for dynamic market adaptation",
        ],
        tools:["MT5 + VPS","MyFXBook EA monitoring","MQL5 marketplace","ChatGPT for MQL5"],
        milestone:"EA live on demo with VPS for 30 days. Compare EA performance vs manual trading of the same signals.",
      },
      {
        week:"W12", title:"Live Capital & NextGen Scaling Protocols", tag:"GRADUATION", tagColor:C.green,
        items:[
          "Rule 1: Only go live after 3 months of profitable demo trading",
          "Path A — Own capital: Start micro ($200–$500), same rules, different emotional weight",
          "Path B — NextGen Funding: Trade $5K–$200K of their capital, keep up to 90% profits",
          "Scaling: Hit 10% net over 4 months → account bumped 25% automatically",
          "Profit split increases to 90% on newly scaled accounts — path to $1M max",
          "Broker selection: IC Markets, Pepperstone — regulated (FCA, ASIC, CySEC)",
          "Monthly P&L review: Am I on track? What's my edge score this month?",
        ],
        tools:["NextGen Funding dashboard","IC Markets / Pepperstone","Notion monthly review template"],
        milestone:"First 30 days funded/live: follow every rule perfectly. Profitability is secondary. The goal is flawless execution of your system.",
      },
    ],
    daily:[
      {time:"10 min",task:"Morning brief: DXY, BTC, indices bias + news scan"},
      {time:"20 min",task:"Execute trades (funded/demo) with full checklist compliance"},
      {time:"10 min",task:"Mid-session check: Are open trades respecting the plan?"},
      {time:"15 min",task:"EOD journal: Trade grades, emotions log, improvement note"},
      {time:"10 min",task:"Quant review: Update performance metrics in your dashboard"},
    ],
    checkpoint:"End of Month 3: Complete trading system built — manual strategy, EA version, 20-setup playbook, 3 months performance data. Ready to attempt the NextGen Funding Standard Challenge.",
  },
];

const pareto = [
  {rank:"1",concept:"Market Structure (BOS/CHoCH)",impact:"Eliminates 70% of bad trades. Keeps you with the trend.",market:"All"},
  {rank:"2",concept:"Risk Management (1% Rule)",impact:"Ensures survival long enough to become consistently profitable.",market:"All"},
  {rank:"3",concept:"Multi-Timeframe Analysis",impact:"Prevents counter-trend entries — the #1 retail mistake.",market:"All"},
  {rank:"4",concept:"Order Blocks & FVGs",impact:"Institutional entry zones — highest R:R setups available.",market:"FX + Indices"},
  {rank:"5",concept:"Liquidity / Stop Hunts",impact:"Stops you being hunted. Turns traps into entries.",market:"All"},
  {rank:"6",concept:"Session Timing (London/NY)",impact:"90% of best setups occur in 2 sessions. Work smarter.",market:"FX + Indices"},
  {rank:"7",concept:"DXY Correlation",impact:"One indicator that explains movements across 6+ markets.",market:"FX + Crypto"},
  {rank:"8",concept:"News Calendar (NFP, CPI, FOMC)",impact:"Avoids account-destroying surprise moves. Critical.",market:"FX + Indices"},
  {rank:"9",concept:"Expectancy & Backtesting",impact:"Proves your system works BEFORE risking real money.",market:"All"},
  {rank:"10",concept:"Trade Psychology Systems",impact:"The only difference between successful traders and the rest.",market:"All"},
];

const resources = [
  {category:"FREE FOUNDATIONS",color:C.cyan,items:[
    {name:"BabyPips School of Pipsology",type:"Course",note:"Best free forex curriculum on the internet"},
    {name:"ICT (Inner Circle Trader) YouTube",type:"Video",note:"Free institutional concepts — 1000+ hours"},
    {name:"TradingView (Free Tier)",type:"Platform",note:"Chart analysis + Pine Script + community ideas"},
    {name:"ForexFactory Calendar",type:"Tool",note:"Bookmark and check daily — non-negotiable"},
  ]},
  {category:"AI & AUTOMATION TOOLS",color:C.purple,items:[
    {name:"ChatGPT / Claude",type:"AI",note:"Strategy analysis, journal summarization, MQL5 code writing"},
    {name:"TrendSpider",type:"AI Charts",note:"Automated technical analysis + AI pattern recognition"},
    {name:"Glassnode",type:"On-Chain",note:"Bitcoin/crypto smart money flow data"},
    {name:"Unusual Whales",type:"Flow Data",note:"Options flow for indices — smart money signal"},
  ]},
  {category:"QUANT & BACKTESTING",color:C.green,items:[
    {name:"MT5 Strategy Tester",type:"Platform",note:"Built-in backtester for EA development"},
    {name:"QuantConnect (LEAN)",type:"Code",note:"Free algorithmic trading backtesting framework"},
    {name:"Python + backtesting.py",type:"Code",note:"Simple backtesting library for retail quants"},
    {name:"TradingView Pine Script",type:"Code",note:"Write your own indicators — no prior coding needed"},
  ]},
  {category:"TRADING PSYCHOLOGY",color:C.gold,items:[
    {name:"Trading in the Zone — Mark Douglas",type:"Book",note:"The definitive trading psychology bible"},
    {name:"TraderVue / Edgewonk",type:"Journal",note:"Data-driven trade journaling with analytics"},
    {name:"Reminiscences of a Stock Operator",type:"Book",note:"Jesse Livermore's timeless wisdom"},
    {name:"Market Wizards — Jack Schwager",type:"Book",note:"How the greatest traders think and operate"},
  ]},
];

// ─── SAGEMASTER PRODUCT SUITE ─────────────────────────────────────────────────
const SAGEMASTER_PRODUCTS = [
  {
    id: "ai-screener",
    name: "AI Screener",
    icon: "🔍",
    tagline: "Find the best setups across 100+ assets in seconds",
    desc: "AI-powered scanner that filters the entire market to surface only high-probability setups matching your criteria. No more manually scanning charts one by one.",
    useCase: "Run every Sunday before the week opens. Filter by: EMA stack aligned + ADX > 25 + volume spike. This becomes your weekly FLEXX SCAN watchlist automatically.",
    url: "https://sagemaster.com",
    tag: "WEEKLY SCAN",
    color: "#4A9EFF",
  },
  {
    id: "deepsage",
    name: "DeepSage AI Analysis",
    icon: "🧠",
    tagline: "Institutional-grade AI market analysis on demand",
    desc: "Deep AI analysis engine that reads market structure, sentiment, and pattern data across multiple timeframes. Think of it as a junior analyst who never sleeps.",
    useCase: "Before entering any major trade, run DeepSage on the asset. Cross-reference its reading with your FLEXX checklist. If both align — high conviction. If they disagree — wait.",
    url: "https://sagemaster.com",
    tag: "PRE-TRADE",
    color: "#C9A227",
  },
  {
    id: "helix",
    name: "Helix",
    icon: "🌀",
    tagline: "Advanced pattern recognition + entry signal engine",
    desc: "Identifies complex chart patterns, FCS channels, and Elliott Wave structures automatically. Saves hours of manual charting work.",
    useCase: "Use Helix to validate your FCS channel entries. If Helix confirms the same E1/E2/E3 entry zone you identified manually, your confidence in the trade increases significantly.",
    url: "https://sagemaster.com",
    tag: "ENTRY CONFIRMATION",
    color: "#9B59B6",
  },
  {
    id: "ai-grid",
    name: "AI Grid",
    icon: "⚡",
    tagline: "Automated grid trading for ranging market conditions",
    desc: "Deploys intelligent grid strategies during sideways/ranging markets. While trend-following setups rest, AI Grid harvests the chop profitably.",
    useCase: "When ADX is below 20 (no trend), activate AI Grid on stable pairs like BTC/USDT or ETH/USDT. Set upper and lower bounds at key S/R levels. Let it work while you focus on learning.",
    url: "https://sagemaster.com",
    tag: "RANGING MARKETS",
    color: "#E67E22",
  },
  {
    id: "ai-dca",
    name: "AI DCA",
    icon: "📊",
    tagline: "Smart dollar-cost averaging with AI-optimized entry timing",
    desc: "Not all DCA is equal. AI DCA identifies statistically optimal entry points within your DCA schedule — buying more when conditions are most favorable, less when they're not.",
    useCase: "Perfect for long-term crypto positions (BTC, ETH). Set a monthly budget, let AI DCA distribute entries across the month at optimal price points instead of fixed calendar dates.",
    url: "https://sagemaster.com",
    tag: "LONG-TERM ACCUMULATION",
    color: "#2ECC71",
  },
  {
    id: "ai-omni",
    name: "AI Omni",
    icon: "🌐",
    tagline: "All-in-one multi-strategy AI trading system",
    desc: "The flagship tool. Combines screening, pattern recognition, entry signals, and risk management into a unified AI trading framework.",
    useCase: "Intermediate to advanced use. Once you've mastered the FLEXX manual system and understand WHY each signal fires, migrate your best setups into AI Omni for systematic execution.",
    url: "https://sagemaster.com",
    tag: "ADVANCED · FLAGSHIP",
    color: "#C9A227",
  },
  {
    id: "ai-strategy-pack",
    name: "AI Strategy Pack",
    icon: "📦",
    tagline: "Pre-built, battle-tested trading strategy templates",
    desc: "Library of ready-to-deploy trading strategies covering trend-following, momentum, mean-reversion, and breakout approaches — all validated with historical data.",
    useCase: "Start with the 'FLEXX EMA Stack' strategy template. Deploy it in Demo mode first. Run 50 trades. Check your hit ratio and edge ratio. Then decide if it's ready for live capital.",
    url: "https://sagemaster.com",
    tag: "STRATEGY TEMPLATES",
    color: "#4A9EFF",
  },
  {
    id: "intelligent-backtest",
    name: "Intelligent Backtest",
    icon: "📈",
    tagline: "Test any strategy against years of historical data instantly",
    desc: "Before putting real money on any strategy, backtest it across 1-5 years of historical data. See win rate, max drawdown, and expected returns before risking a cent.",
    useCase: "Rule: Never trade a strategy live that hasn't been backtested with minimum 100 trades. Use Intelligent Backtest to validate your FLEXX entries. 40%+ win rate + 2x edge = deployable.",
    url: "https://sagemaster.com",
    tag: "VALIDATION ESSENTIAL",
    color: "#E74C3C",
  },
  {
    id: "magic-terminal",
    name: "Magic Terminal",
    icon: "⌨️",
    tagline: "Professional order execution + trade management terminal",
    desc: "Execute trades with precision. Advanced order types, OCO orders, bracket orders, and automated trail stops. The professional's execution environment.",
    useCase: "Once your strategy is validated, use Magic Terminal for all live entries. Set your stop + target + trail stop in one click at entry time. This removes all emotional decision-making during the trade.",
    url: "https://sagemaster.com",
    tag: "LIVE EXECUTION",
    color: "#1ABC9C",
  },
];


export default function SkillAcquisitionMap() {
  const [activeMonth, setActiveMonth] = useState(0);
  const [activeTab, setActiveTab] = useState("skills");
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [ngTab, setNgTab] = useState("overview");
  const [selectedChallenge, setSelectedChallenge] = useState(0);
  const [mainSection, setMainSection] = useState("roadmap");

  // ── v5 features ──
  const [sageMasterOpen, setSageMasterOpen] = useState(false);
  const [sageMasterProduct, setSageMasterProduct] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [lbName, setLbName] = useState("");
  const [lbSubmitted, setLbSubmitted] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncCode, setSyncCode] = useState("");
  const [syncImportInput, setSyncImportInput] = useState("");
  const [syncImportError, setSyncImportError] = useState("");
  const [syncImportSuccess, setSyncImportSuccess] = useState(false);
  const [streakData, setStreakData] = useState({ streak: 0, lastVisit: null, totalDays: 0 });
  const [completedMonths, setCompletedMonths] = useState({});


  // ── Persistence ──
  useEffect(() => {
    try {
      const s = localStorage.getItem("flexx_rm_v5");
      if (s) {
        const d = JSON.parse(s);
        if (d.activeMonth !== undefined) setActiveMonth(d.activeMonth);
        if (d.mainSection) setMainSection(d.mainSection);
        if (d.activeTab) setActiveTab(d.activeTab);
        if (d.completedMonths) setCompletedMonths(d.completedMonths);
        if (d.lbName) setLbName(d.lbName);
        if (d.lbSubmitted) setLbSubmitted(d.lbSubmitted);
      }
      const lb = localStorage.getItem("flexx_rm_v5_lb");
      if (lb) setLeaderboardData(JSON.parse(lb));
    } catch(e) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem("flexx_rm_v5", JSON.stringify({ activeMonth, mainSection, activeTab, completedMonths, lbName, lbSubmitted })); }
    catch(e) {}
  }, [activeMonth, mainSection, activeTab, completedMonths, lbName, lbSubmitted]);

  useEffect(() => {
    try {
      const today = new Date().toDateString();
      const saved = JSON.parse(localStorage.getItem("flexx_rm_v5_streak") || "{}");
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      let streak = saved.streak || 0, totalDays = saved.totalDays || 0;
      if (saved.lastVisit !== today) {
        totalDays += 1;
        if (saved.lastVisit === yesterday) streak += 1; else streak = 1;
        const u = { streak, lastVisit: today, totalDays };
        localStorage.setItem("flexx_rm_v5_streak", JSON.stringify(u));
        setStreakData(u);
      } else { setStreakData({ streak, lastVisit: today, totalDays: saved.totalDays || 1 }); }
    } catch(e) { setStreakData({ streak: 1, lastVisit: new Date().toDateString(), totalDays: 1 }); }
  }, []);

  // ── Search ──
  const runSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    const lower = q.toLowerCase();
    const results = [];
    roadmap.forEach((mo, moIdx) => {
      const moText = [mo.title, mo.subtitle, mo.focus, mo.goal].filter(Boolean).join(" ").toLowerCase();
      if (moText.includes(lower)) results.push({ type:"month", label:`Month ${moIdx+1}: ${mo.title}`, action:()=>{ setActiveMonth(moIdx); setMainSection("roadmap"); setSearchOpen(false); } });
      (mo.skills||[]).forEach(sk => {
        const skText = [sk.name, sk.why, ...(sk.subSkills||[])].filter(Boolean).join(" ").toLowerCase();
        if (skText.includes(lower)) results.push({ type:"skill", label:`${sk.name}`, sub:`Month ${moIdx+1}`, action:()=>{ setActiveMonth(moIdx); setMainSection("roadmap"); setActiveTab("skills"); setExpandedSkill(sk.id); setSearchOpen(false); } });
      });
    });
    setSearchResults(results.slice(0,15));
  };
  useEffect(() => {
    const handler = (e) => { if ((e.metaKey||e.ctrlKey)&&e.key==="k") { e.preventDefault(); setSearchOpen(true); } if (e.key==="Escape") setSearchOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => { if (searchOpen&&searchInputRef.current) setTimeout(()=>searchInputRef.current?.focus(),50); }, [searchOpen]);

  // ── Sync ──
  const buildSyncCode = () => {
    try { setSyncCode(btoa(JSON.stringify({ activeMonth, mainSection, activeTab, completedMonths, lbName, streakData, exported:new Date().toISOString(), version:"v5_rm" }))); }
    catch(e) { setSyncCode("ERROR"); }
  };
  const importSyncCode = () => {
    setSyncImportError(""); setSyncImportSuccess(false);
    try {
      const d = JSON.parse(atob(syncImportInput.trim()));
      if (d.version!=="v5_rm") throw new Error("ver");
      if (d.activeMonth !== undefined) setActiveMonth(d.activeMonth);
      if (d.mainSection) setMainSection(d.mainSection);
      if (d.activeTab) setActiveTab(d.activeTab);
      if (d.completedMonths) setCompletedMonths(d.completedMonths);
      if (d.lbName) setLbName(d.lbName);
      setSyncImportSuccess(true); setSyncImportInput("");
    } catch(e) { setSyncImportError("Invalid sync code."); }
  };

  // ── Leaderboard ──
  const roadmapProgress = Math.round(Object.values(completedMonths).filter(Boolean).length / roadmap.length * 100);
  const submitToLeaderboard = () => {
    if (!lbName.trim()) return;
    const entry = { name:lbName.trim(), completed:Object.values(completedMonths).filter(Boolean).length, total:roadmap.length, pct:roadmapProgress, streak:streakData.streak, track:"ROADMAP", date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}) };
    try {
      const existing = JSON.parse(localStorage.getItem("flexx_rm_v5_lb")||"[]");
      const updated = [entry,...existing.filter(e=>e.name!==entry.name)].sort((a,b)=>b.pct-a.pct||b.streak-a.streak).slice(0,20);
      localStorage.setItem("flexx_rm_v5_lb", JSON.stringify(updated));
      setLeaderboardData(updated); setLbSubmitted(true);
    } catch(e) {}
  };

  const toggleMonthComplete = (idx) => setCompletedMonths(p => ({ ...p, [idx]: !p[idx] }));

  const month = roadmap[activeMonth];


  // ══ SAGEMASTER MODAL ══
  const SageMasterModal = () => {
    const prod = sageMasterProduct ? SAGEMASTER_PRODUCTS.find(p => p.id === sageMasterProduct) : null;
    return (
      <div onClick={() => setSageMasterOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:10000, display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"40px 16px", overflowY:"auto" }}>
        <div onClick={e=>e.stopPropagation()} style={{ maxWidth:800, width:"100%", background:C.bg, border:`1px solid ${C.border}`, padding:0 }}>
          <div style={{ padding:"18px 22px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontSize:8, color:"#4A9EFF", letterSpacing:3, fontFamily:"monospace" }}>⚡ SAGEMASTER AI SUITE</div>
              <div style={{ fontSize:16, color:C.white, fontWeight:700, marginTop:4 }}>AI-Powered Trading Tools</div>
            </div>
            <button onClick={()=>setSageMasterOpen(false)} style={{ background:"transparent", border:"none", color:C.muted, fontSize:18, cursor:"pointer" }}>✕</button>
          </div>
          <div style={{ padding:"18px 22px" }}>
            {!prod ? (
              <>
                <div style={{ fontSize:12, color:C.gray, lineHeight:1.7, marginBottom:16 }}>FLEXX Academy's recommended AI infrastructure. Click any tool for details and direct access.</div>
                <div style={{ display:"grid", gap:10 }}>
                  {SAGEMASTER_PRODUCTS.map(p => (
                    <div key={p.id} onClick={() => setSageMasterProduct(p.id)} style={{ padding:"14px 16px", background:C.card, border:`1px solid ${p.color}30`, borderLeft:`3px solid ${p.color}`, cursor:"pointer", transition:"all 0.2s" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", gap:8, alignItems:"baseline", flexWrap:"wrap", marginBottom:4 }}>
                            <span style={{ fontSize:18 }}>{p.icon}</span>
                            <span style={{ fontSize:14, fontWeight:700, color:C.white }}>{p.name}</span>
                            <span style={{ fontSize:8, padding:"2px 7px", color:p.color, border:`1px solid ${p.color}40`, fontFamily:"monospace", letterSpacing:1 }}>{p.tag}</span>
                          </div>
                          <div style={{ fontSize:11, color:C.gray, lineHeight:1.5 }}>{p.tagline}</div>
                        </div>
                        <span style={{ color:C.gold, fontSize:14, flexShrink:0 }}>›</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div>
                <button onClick={() => setSageMasterProduct(null)} style={{ background:"transparent", border:"none", color:C.muted, cursor:"pointer", fontFamily:"monospace", fontSize:11, marginBottom:16 }}>← ALL TOOLS</button>
                <div style={{ fontSize:32, marginBottom:8 }}>{prod.icon}</div>
                <div style={{ fontSize:8, color:prod.color, letterSpacing:3, marginBottom:6, fontFamily:"monospace" }}>{prod.tag}</div>
                <div style={{ fontSize:22, fontWeight:700, color:C.white, marginBottom:8 }}>{prod.name}</div>
                <div style={{ fontSize:14, color:C.gray, fontStyle:"italic", lineHeight:1.7, marginBottom:16 }}>{prod.tagline}</div>
                <div style={{ padding:"14px 16px", background:`${prod.color}10`, border:`1px solid ${prod.color}30`, marginBottom:16 }}>
                  <div style={{ fontSize:8, color:prod.color, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>◆ WHAT IT DOES</div>
                  <div style={{ fontSize:13, color:C.gray, lineHeight:1.75 }}>{prod.desc}</div>
                </div>
                <div style={{ padding:"14px 16px", background:C.goldFaint, border:`1px solid ${C.gold}30`, borderLeft:`3px solid ${C.gold}`, marginBottom:20 }}>
                  <div style={{ fontSize:8, color:C.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>⚡ FLEXX USE CASE</div>
                  <div style={{ fontSize:13, color:C.gray, lineHeight:1.75 }}>{prod.useCase}</div>
                </div>
                <a href={prod.url} target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"10px 22px", background:C.gold, color:C.bg, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700, textDecoration:"none" }}>
                  OPEN {prod.name.toUpperCase()} →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ══ SEARCH MODAL ══
  const SearchModal = () => (
    <div onClick={() => setSearchOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:10000, display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"80px 16px 16px" }}>
      <div onClick={e=>e.stopPropagation()} style={{ maxWidth:680, width:"100%", background:C.bg, border:`1px solid ${C.border}` }}>
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:10, alignItems:"center" }}>
          <span style={{ fontSize:14 }}>🔍</span>
          <input ref={searchInputRef} value={searchQuery} onChange={e=>runSearch(e.target.value)} placeholder="Search roadmap months, skills, concepts..."
            style={{ flex:1, background:"transparent", border:"none", color:C.white, fontSize:14, outline:"none", fontFamily:"monospace" }} />
          <span style={{ fontSize:9, color:C.muted, fontFamily:"monospace", letterSpacing:1 }}>ESC</span>
        </div>
        <div style={{ maxHeight:420, overflowY:"auto" }}>
          {searchResults.length === 0 && searchQuery && (
            <div style={{ padding:"24px 18px", textAlign:"center", color:C.muted, fontSize:12, fontFamily:"monospace" }}>No results for "{searchQuery}"</div>
          )}
          {!searchQuery && (
            <div style={{ padding:"24px 18px", textAlign:"center", color:C.muted, fontSize:11, fontFamily:"monospace" }}>Type to search across your roadmap · Press ⌘K anywhere</div>
          )}
          {searchResults.map((r, i) => (
            <div key={i} onClick={r.action} style={{ padding:"12px 18px", borderBottom:`1px solid ${C.border}`, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ fontSize:8, color:r.type==="month"?C.cyan:C.gold, letterSpacing:2, fontFamily:"monospace", marginBottom:4 }}>{r.type.toUpperCase()} {r.sub ? `· ${r.sub}` : ""}</div>
              <div style={{ fontSize:13, color:C.white }}>{r.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ══ SYNC MODAL ══
  const SyncModal = () => (
    <div onClick={() => setSyncOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:10000, display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"40px 16px", overflowY:"auto" }}>
      <div onClick={e=>e.stopPropagation()} style={{ maxWidth:540, width:"100%", background:C.bg, border:`1px solid ${C.border}` }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:8, color:C.gold, letterSpacing:3, fontFamily:"monospace" }}>📱 CROSS-DEVICE SYNC</div>
            <div style={{ fontSize:14, color:C.white, fontWeight:700, marginTop:4 }}>Move your progress between devices</div>
          </div>
          <button onClick={()=>setSyncOpen(false)} style={{ background:"transparent", border:"none", color:C.muted, fontSize:18, cursor:"pointer" }}>✕</button>
        </div>
        <div style={{ padding:"18px 20px" }}>
          <div style={{ marginBottom:22 }}>
            <div style={{ fontSize:9, color:C.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>EXPORT — GENERATE A SYNC CODE</div>
            <button onClick={buildSyncCode} style={{ padding:"8px 16px", fontSize:10, letterSpacing:2, fontFamily:"monospace", background:C.gold, color:C.bg, border:"none", cursor:"pointer", marginBottom:10 }}>GENERATE CODE</button>
            {syncCode && (
              <>
                <textarea readOnly value={syncCode} style={{ width:"100%", minHeight:80, padding:10, background:C.card, border:`1px solid ${C.border}`, color:C.white, fontSize:10, fontFamily:"monospace", resize:"vertical", outline:"none", wordBreak:"break-all" }} />
                <div style={{ fontSize:10, color:C.muted, marginTop:6 }}>Copy this code and paste it on your other device to sync.</div>
              </>
            )}
          </div>
          <div>
            <div style={{ fontSize:9, color:C.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>IMPORT — PASTE A SYNC CODE</div>
            <textarea value={syncImportInput} onChange={e=>setSyncImportInput(e.target.value)} placeholder="Paste sync code here..."
              style={{ width:"100%", minHeight:80, padding:10, background:C.card, border:`1px solid ${C.border}`, color:C.white, fontSize:10, fontFamily:"monospace", resize:"vertical", outline:"none", marginBottom:10 }} />
            <button onClick={importSyncCode} style={{ padding:"8px 16px", fontSize:10, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:C.gold, border:`1px solid ${C.gold}`, cursor:"pointer" }}>IMPORT</button>
            {syncImportError && <div style={{ fontSize:11, color:"#E74C3C", marginTop:10, fontFamily:"monospace" }}>✗ {syncImportError}</div>}
            {syncImportSuccess && <div style={{ fontSize:11, color:"#2ECC71", marginTop:10, fontFamily:"monospace" }}>✓ Sync imported successfully</div>}
          </div>
        </div>
      </div>
    </div>
  );

  // ══ LEADERBOARD MODAL ══
  const LeaderboardModal = () => (
    <div onClick={() => setShowLeaderboard(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:10000, display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"40px 16px", overflowY:"auto" }}>
      <div onClick={e=>e.stopPropagation()} style={{ maxWidth:640, width:"100%", background:C.bg, border:`1px solid ${C.border}` }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:8, color:"#2ECC71", letterSpacing:3, fontFamily:"monospace" }}>🏆 LEADERBOARD</div>
            <div style={{ fontSize:14, color:C.white, fontWeight:700, marginTop:4 }}>Top Roadmap Progress</div>
          </div>
          <button onClick={()=>setShowLeaderboard(false)} style={{ background:"transparent", border:"none", color:C.muted, fontSize:18, cursor:"pointer" }}>✕</button>
        </div>
        <div style={{ padding:"18px 20px" }}>
          {!lbSubmitted ? (
            <div style={{ marginBottom:18 }}>
              <div style={{ fontSize:10, color:C.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>SUBMIT YOUR PROGRESS</div>
              <div style={{ display:"flex", gap:8 }}>
                <input value={lbName} onChange={e=>setLbName(e.target.value)} placeholder="Your name"
                  style={{ flex:1, padding:10, background:C.card, border:`1px solid ${C.border}`, color:C.white, fontSize:12, fontFamily:"monospace", outline:"none" }} />
                <button onClick={submitToLeaderboard} style={{ padding:"10px 16px", fontSize:10, letterSpacing:2, fontFamily:"monospace", background:"#2ECC71", color:C.bg, border:"none", cursor:"pointer" }}>SUBMIT</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize:10, color:"#2ECC71", marginBottom:14, fontFamily:"monospace" }}>✓ Submitted as {lbName} — {roadmapProgress}%</div>
          )}
          <div style={{ border:`1px solid ${C.border}` }}>
            <div style={{ display:"grid", gridTemplateColumns:"40px 1fr 60px 60px", padding:"10px 12px", background:C.card, fontSize:8, color:C.muted, letterSpacing:2, fontFamily:"monospace" }}>
              <div>#</div><div>NAME</div><div style={{ textAlign:"right" }}>%</div><div style={{ textAlign:"right" }}>STREAK</div>
            </div>
            {leaderboardData.length === 0 ? (
              <div style={{ padding:20, textAlign:"center", fontSize:11, color:C.muted, fontFamily:"monospace" }}>No entries yet — be first!</div>
            ) : leaderboardData.map((e, i) => (
              <div key={e.name+i} style={{ display:"grid", gridTemplateColumns:"40px 1fr 60px 60px", padding:"10px 12px", borderTop:`1px solid ${C.border}`, fontSize:11, fontFamily:"monospace", background:e.name===lbName?`${C.gold}08`:"transparent" }}>
                <div style={{ color:i<3?C.gold:C.muted, fontWeight:700 }}>{i+1}</div>
                <div style={{ color:C.white }}>{e.name}</div>
                <div style={{ textAlign:"right", color:"#2ECC71" }}>{e.pct}%</div>
                <div style={{ textAlign:"right", color:"#FF6B35" }}>🔥{e.streak}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );


  return (
    <div style={{background:C.bg,minHeight:"100vh",fontFamily:"'Courier New',monospace",color:C.white}}>

      {sageMasterOpen && <SageMasterModal />}
      {showLeaderboard && <LeaderboardModal />}
      {searchOpen && <SearchModal />}
      {syncOpen && <SyncModal />}

      {/* HEADER */}
      <div style={{background:"linear-gradient(135deg,#0D1117 0%,#080A0E 100%)",borderBottom:`1px solid ${C.border}`,padding:"28px 20px 20px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:`radial-gradient(ellipse at top right,${C.goldGlow},transparent 60%)`,pointerEvents:"none"}}/>
        <div style={{maxWidth:900,margin:"0 auto",position:"relative"}}>
          <div style={{fontSize:9,color:C.gold,letterSpacing:4,marginBottom:10}}>◆ FLEXX ACADEMY · SKILL ACQUISITION MAP · 2026</div>
          <div style={{fontSize:22,fontWeight:900,color:C.white,letterSpacing:-0.5,lineHeight:1.2}}>INTERMEDIATE → EXPERT</div>
          <div style={{fontSize:12,color:C.gold,letterSpacing:2,marginTop:4}}>FOREX · CRYPTO · INDICES · EA · QUANT · AI TRADING · PROP FUNDING</div>
          <div style={{marginTop:14,display:"flex",gap:8,flexWrap:"wrap"}}>
            {["3 MONTHS","30-90 MIN/DAY","3 MARKETS","EA DEPLOYMENT","NEXTGEN FUNDED"].map(tag=>(
              <div key={tag} style={{padding:"3px 10px",fontSize:8,letterSpacing:2,border:`1px solid ${C.gold}40`,color:C.gold,background:C.goldFaint}}>{tag}</div>
            ))}
          </div>
          <div style={{marginTop:14,display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>setSearchOpen(true)} style={{padding:"6px 12px",fontSize:9,letterSpacing:2,fontFamily:"monospace",background:"transparent",color:C.gray,border:`1px solid ${C.border}`,cursor:"pointer"}}>🔍 SEARCH (⌘K)</button>
            <button onClick={()=>{setSageMasterProduct(null);setSageMasterOpen(true);}} style={{padding:"6px 12px",fontSize:9,letterSpacing:2,fontFamily:"monospace",background:"transparent",color:"#4A9EFF",border:"1px solid #4A9EFF60",cursor:"pointer"}}>⚡ SAGEMASTER</button>
            <button onClick={()=>setShowLeaderboard(true)} style={{padding:"6px 12px",fontSize:9,letterSpacing:2,fontFamily:"monospace",background:"transparent",color:"#2ECC71",border:"1px solid #2ECC7160",cursor:"pointer"}}>🏆 LEADERBOARD</button>
            <button onClick={()=>setSyncOpen(true)} style={{padding:"6px 12px",fontSize:9,letterSpacing:2,fontFamily:"monospace",background:"transparent",color:C.gold,border:`1px solid ${C.gold}40`,cursor:"pointer"}}>📱 SYNC</button>
          </div>
        </div>
      </div>

      <div style={{maxWidth:900,margin:"0 auto",padding:"20px 16px"}}>

        {/* MAIN NAV */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:18}}>
          {[
            {label:"PROGRESS",val:`${roadmapProgress}%`,col:C.gold},
            {label:"🔥 STREAK",val:`${streakData.streak}d`,col:"#FF6B35"},
            {label:"MONTH",val:`${activeMonth+1}/${roadmap.length}`,col:C.cyan},
            {label:"DAYS ACTIVE",val:`${streakData.totalDays}`,col:"#4A9EFF"},
          ].map(stat=>(
            <div key={stat.label} style={{padding:"10px 12px",background:C.card,border:`1px solid ${C.border}`,textAlign:"center"}}>
              <div style={{fontSize:18,fontWeight:900,color:stat.col,fontFamily:"monospace",lineHeight:1.1}}>{stat.val}</div>
              <div style={{fontSize:7,color:C.muted,letterSpacing:2,fontFamily:"monospace",marginTop:4}}>{stat.label}</div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:4,marginBottom:20,flexWrap:"wrap"}}>
          {[
            {id:"roadmap",label:"3-MONTH ROADMAP",color:C.cyan},
            {id:"nextgen",label:"◆ NEXTGEN FUNDING PATH",color:C.gold},
            {id:"pareto",label:"80/20 CONCEPTS",color:C.green},
            {id:"resources",label:"RESOURCES",color:C.purple},
          ].map(nav=>(
            <button key={nav.id} onClick={()=>setMainSection(nav.id)} style={{
              padding:"8px 14px",fontSize:8,letterSpacing:2,cursor:"pointer",
              background:mainSection===nav.id?nav.color+"20":C.card,
              border:`1px solid ${mainSection===nav.id?nav.color:C.border}`,
              color:mainSection===nav.id?nav.color:C.gray,transition:"all 0.2s",
            }}>{nav.label}</button>
          ))}
        </div>

        {/* ══════ ROADMAP SECTION ══════ */}
        {mainSection==="roadmap"&&(
          <>
            <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
              {roadmap.map((m,i)=>(
                <button key={i} onClick={()=>{setActiveMonth(i);setExpandedSkill(null);setActiveTab("skills");}} style={{
                  flex:1,minWidth:120,padding:"12px 8px",
                  background:activeMonth===i?m.color+"20":C.card,
                  border:`1px solid ${activeMonth===i?m.color:C.border}`,
                  color:activeMonth===i?m.color:C.gray,
                  cursor:"pointer",fontSize:8,letterSpacing:2,transition:"all 0.2s",
                }}>
                  <div style={{fontSize:16,marginBottom:4}}>{m.icon}</div>
                  <div style={{fontWeight:700}}>{m.month}</div>
                  <div style={{marginTop:2,fontSize:7,color:activeMonth===i?m.color+"CC":C.muted}}>{m.subtitle}</div>
                </button>
              ))}
            </div>

            <div style={{background:C.card,border:`1px solid ${month.color}40`,borderLeft:`4px solid ${month.color}`,padding:"16px 20px",marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:8,color:month.color,letterSpacing:3,marginBottom:6}}>{month.month} · {month.weeks} · {month.subtitle.toUpperCase()}</div>
                  <div style={{fontSize:13,color:C.grayLight,lineHeight:1.6}}>{month.focus}</div>
                </div>
                <button onClick={()=>toggleMonthComplete(activeMonth)} style={{padding:"8px 14px",fontSize:9,letterSpacing:2,fontFamily:"monospace",fontWeight:700,background:completedMonths[activeMonth]?month.color:"transparent",color:completedMonths[activeMonth]?C.bg:month.color,border:`1px solid ${month.color}`,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {completedMonths[activeMonth] ? "✓ MONTH COMPLETE" : "MARK COMPLETE"}
                </button>
              </div>
              <div style={{marginTop:14}}>
                <div style={{fontSize:8,color:month.color,letterSpacing:2,marginBottom:8}}>▸ THE 20% THAT GIVES 80% OF RESULTS THIS MONTH</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  {month.pareto.map((p,i)=>(
                    <div key={i} style={{padding:"8px 12px",background:month.faint,border:`1px solid ${month.color}25`,fontSize:10,color:C.grayLight,lineHeight:1.5}}>
                      <span style={{color:month.color,marginRight:6}}>→</span>{p}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:`1px solid ${C.border}`}}>
              {[{id:"skills",label:"SKILL BREAKDOWN"},{id:"daily",label:"DAILY ROUTINE"},{id:"checkpoint",label:"MILESTONE"}].map(tab=>(
                <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
                  padding:"8px 14px",fontSize:8,letterSpacing:2,background:"transparent",
                  borderBottom:`2px solid ${activeTab===tab.id?month.color:"transparent"}`,
                  color:activeTab===tab.id?month.color:C.muted,cursor:"pointer",border:"none",
                }}>{tab.label}</button>
              ))}
            </div>

            {activeTab==="skills"&&(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {month.skills.map((skill,si)=>(
                  <div key={si} style={{background:C.card,border:`1px solid ${expandedSkill===si?month.color+"60":C.border}`,transition:"border-color 0.2s"}}>
                    <button onClick={()=>setExpandedSkill(expandedSkill===si?null:si)} style={{width:"100%",padding:"14px 16px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                          <span style={{fontSize:7,color:month.color,letterSpacing:2}}>{skill.week}</span>
                          <span style={{fontSize:7,letterSpacing:1,padding:"2px 7px",background:skill.tagColor+"20",border:`1px solid ${skill.tagColor}50`,color:skill.tagColor}}>{skill.tag}</span>
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:C.white}}>{skill.title}</div>
                      </div>
                      <div style={{color:month.color,fontSize:16,flexShrink:0}}>{expandedSkill===si?"−":"+"}</div>
                    </button>
                    {expandedSkill===si&&(
                      <div style={{padding:"0 16px 16px",borderTop:`1px solid ${C.border}`}}>
                        <div style={{paddingTop:14}}>
                          <div style={{fontSize:8,color:C.gray,letterSpacing:2,marginBottom:8}}>SUB-SKILLS TO MASTER (IN ORDER)</div>
                          {skill.items.map((item,ii)=>(
                            <div key={ii} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:ii<skill.items.length-1?`1px solid ${C.border}`:"none"}}>
                              <span style={{color:month.color,fontSize:10,flexShrink:0,marginTop:1}}>{String(ii+1).padStart(2,"0")}</span>
                              <span style={{fontSize:11,color:C.grayLight,lineHeight:1.5}}>{item}</span>
                            </div>
                          ))}
                          <div style={{marginTop:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                            <div style={{padding:12,background:C.bg,border:`1px solid ${C.border}`}}>
                              <div style={{fontSize:8,color:month.color,letterSpacing:2,marginBottom:6}}>◈ TOOLS</div>
                              {skill.tools.map((t,ti)=>(
                                <div key={ti} style={{fontSize:10,color:C.gray,marginBottom:3,lineHeight:1.4}}>→ {t}</div>
                              ))}
                            </div>
                            <div style={{padding:12,background:C.bg,border:`1px solid ${month.color}30`}}>
                              <div style={{fontSize:8,color:month.color,letterSpacing:2,marginBottom:6}}>✓ MILESTONE TEST</div>
                              <div style={{fontSize:10,color:C.grayLight,lineHeight:1.5}}>{skill.milestone}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab==="daily"&&(
              <div style={{background:C.card,border:`1px solid ${C.border}`,padding:20}}>
                <div style={{fontSize:8,color:month.color,letterSpacing:3,marginBottom:16}}>◈ DAILY PRACTICE ROUTINE — {month.month} (30–75 MIN TOTAL)</div>
                {month.daily.map((d,di)=>(
                  <div key={di} style={{display:"flex",gap:14,padding:"12px 0",borderBottom:di<month.daily.length-1?`1px solid ${C.border}`:"none",alignItems:"flex-start"}}>
                    <div style={{minWidth:52,padding:"4px 8px",background:month.color+"20",border:`1px solid ${month.color}40`,textAlign:"center",fontSize:9,color:month.color,letterSpacing:1,flexShrink:0}}>{d.time}</div>
                    <div style={{fontSize:12,color:C.grayLight,lineHeight:1.6}}>{d.task}</div>
                  </div>
                ))}
                <div style={{marginTop:20,padding:14,background:`linear-gradient(135deg,${month.faint},transparent)`,border:`1px solid ${month.color}30`}}>
                  <div style={{fontSize:8,color:month.color,letterSpacing:2,marginBottom:6}}>CONSISTENCY {">"} INTENSITY</div>
                  <div style={{fontSize:11,color:C.grayLight,lineHeight:1.7}}>30 minutes of focused daily practice compounds faster than 5-hour weekend cram sessions. The traders who win show up every day. This routine builds the neural patterns that make good trading automatic.</div>
                </div>
              </div>
            )}

            {activeTab==="checkpoint"&&(
              <div style={{background:C.card,border:`1px solid ${month.color}40`,padding:20}}>
                <div style={{fontSize:8,color:month.color,letterSpacing:3,marginBottom:14}}>✓ END-OF-MONTH CHECKPOINT</div>
                <div style={{fontSize:13,color:C.white,lineHeight:1.8,marginBottom:20}}>{month.checkpoint}</div>
                <div style={{padding:14,background:C.bg,border:`1px dashed ${month.color}40`}}>
                  <div style={{fontSize:8,color:C.gray,letterSpacing:2,marginBottom:10}}>SELF-ASSESSMENT</div>
                  {["Can you explain your trade rationale in under 60 seconds?","Did you follow your risk rules on EVERY trade this month?","Is your system documented enough to hand to someone else?","Are your losses smaller and profits larger than last month?","Do you feel CALMER about trading than 30 days ago?"].map((q,qi)=>(
                    <div key={qi} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
                      <span style={{color:month.color,flexShrink:0}}>□</span>
                      <span style={{fontSize:11,color:C.grayLight,lineHeight:1.5}}>{q}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════ NEXTGEN SECTION ══════ */}
        {mainSection==="nextgen"&&(
          <>
            <div style={{padding:"16px 20px",marginBottom:16,background:`linear-gradient(135deg,${C.goldFaint},${C.greenFaint})`,border:`1px solid ${C.gold}50`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontSize:8,color:C.gold,letterSpacing:3,marginBottom:4}}>◆ FLEXX ACADEMY PROP FIRM PARTNER</div>
                <div style={{fontSize:18,fontWeight:900,color:C.white}}>NEXTGEN FUNDING</div>
                <div style={{fontSize:10,color:C.grayLight,marginTop:2}}>Backed by Errante · thenextgenfunding.com</div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["EA ALLOWED","NEWS TRADING OK","90% SPLIT","FEE REFUNDED","UP TO $200K"].map(f=>(
                  <div key={f} style={{padding:"3px 8px",fontSize:7,letterSpacing:1,background:C.greenFaint,border:`1px solid ${C.green}40`,color:C.green}}>{f}</div>
                ))}
              </div>
            </div>

            <div style={{padding:"10px 16px",marginBottom:16,background:C.redFaint,border:`1px solid ${C.red}40`,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{fontSize:9,color:C.red,letterSpacing:2,fontWeight:700}}>🔥 APRIL PROMOTION</div>
              <div style={{fontSize:11,color:C.grayLight}}>30% OFF 50K, 100K & 200K challenges · Code <span style={{color:C.gold,fontWeight:700}}>PASS30</span> · Valid until 30 April 2026</div>
            </div>

            <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:`1px solid ${C.border}`}}>
              {[{id:"overview",label:"WHY NEXTGEN"},{id:"challenges",label:"CHALLENGES"},{id:"rules",label:"CRITICAL RULES"},{id:"scaling",label:"SCALING PLAN"},{id:"strategy",label:"PASS STRATEGY"}].map(tab=>(
                <button key={tab.id} onClick={()=>setNgTab(tab.id)} style={{
                  padding:"8px 10px",fontSize:8,letterSpacing:2,background:"transparent",
                  borderBottom:`2px solid ${ngTab===tab.id?C.gold:"transparent"}`,
                  color:ngTab===tab.id?C.gold:C.muted,cursor:"pointer",border:"none",
                }}>{tab.label}</button>
              ))}
            </div>

            {ngTab==="overview"&&(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{background:C.card,border:`1px solid ${C.border}`,padding:16}}>
                  <div style={{fontSize:8,color:C.gold,letterSpacing:2,marginBottom:12}}>WHY NEXTGEN FITS YOUR FLEXX CURRICULUM PERFECTLY</div>
                  {[
                    {icon:"✓",title:"EAs Fully Allowed",desc:"The EA you build in Month 2 can run directly on your NextGen challenge account. This is rare — most prop firms ban or restrict EAs heavily."},
                    {icon:"✓",title:"News Trading Allowed (Standard & Rapid)",desc:"Your ForexFactory discipline applies directly. Trade NFP, CPI, FOMC — no extra rule adjustments needed on Standard or Rapid challenges."},
                    {icon:"✓",title:"Balance-Based Drawdown",desc:"Most prop firms use equity-based DD — floating losses count. NextGen uses balance-based: only closed losses count. Far more forgiving for swing traders."},
                    {icon:"✓",title:"Unlimited Trading Days",desc:"No time pressure. Take your time. Your system doesn't need to rush — trade at the pace your strategy demands."},
                    {icon:"✓",title:"Weekend Holding Allowed",desc:"Your swing trade setups don't need to be force-closed Friday. Hold through the weekend without restriction."},
                    {icon:"✓",title:"Fee Fully Refunded on First Payout",desc:"Net cost to become a funded trader = $0. The challenge fee is reimbursed with your very first profit split."},
                    {icon:"✓",title:"Backed by Errante (Regulated Broker)",desc:"Errante is a multi-regulated broker — adds institutional credibility and security to the operation."},
                  ].map((item,i)=>(
                    <div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:i<6?`1px solid ${C.border}`:"none"}}>
                      <span style={{color:C.green,fontSize:14,flexShrink:0,marginTop:1}}>{item.icon}</span>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:C.white,marginBottom:2}}>{item.title}</div>
                        <div style={{fontSize:10,color:C.gray,lineHeight:1.5}}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{padding:16,background:C.card,border:`1px solid ${C.cyan}30`,borderLeft:`4px solid ${C.cyan}`}}>
                  <div style={{fontSize:8,color:C.cyan,letterSpacing:2,marginBottom:10}}>THE FLEXX → NEXTGEN FUNDED TRADER PIPELINE</div>
                  {[
                    {step:"Month 1",action:"Build market structure + risk management foundation"},
                    {step:"Month 2",action:"Build and test your EA on demo for 30+ days"},
                    {step:"Month 3 W11",action:"Run 30-day mock challenge on demo with exact NextGen rules"},
                    {step:"Month 3 W12",action:"Pass mock challenge twice → buy real NextGen Standard Challenge"},
                    {step:"Post M3",action:"Deploy EA on NextGen account via VPS — trade $5K–$200K"},
                    {step:"4 Months Funded",action:"Hit 10% net profit + 1 payout → account scaled 25%"},
                    {step:"16 Months",action:"Path to $400K+ account at 90% profit split"},
                  ].map((s,si)=>(
                    <div key={si} style={{display:"flex",gap:12,marginBottom:6,alignItems:"flex-start"}}>
                      <div style={{minWidth:72,padding:"2px 6px",background:C.cyanFaint,border:`1px solid ${C.cyan}30`,fontSize:8,color:C.cyan,letterSpacing:1,flexShrink:0,textAlign:"center"}}>{s.step}</div>
                      <div style={{fontSize:11,color:C.grayLight,lineHeight:1.5}}>{s.action}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ngTab==="challenges"&&(
              <div>
                <div style={{display:"flex",gap:6,marginBottom:14}}>
                  {nextgenChallenges.map((ch,ci)=>(
                    <button key={ci} onClick={()=>setSelectedChallenge(ci)} style={{
                      flex:1,padding:"10px 8px",cursor:"pointer",
                      background:selectedChallenge===ci?ch.tagColor+"20":C.card,
                      border:`1px solid ${selectedChallenge===ci?ch.tagColor:C.border}`,
                      color:selectedChallenge===ci?ch.tagColor:C.gray,
                      fontSize:9,letterSpacing:1,fontWeight:700,
                    }}>
                      <div>{ch.name}</div>
                      <div style={{fontSize:7,marginTop:3,color:selectedChallenge===ci?ch.tagColor+"CC":C.muted}}>{ch.phases}</div>
                    </button>
                  ))}
                </div>
                {(()=>{
                  const ch=nextgenChallenges[selectedChallenge];
                  return(
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{padding:"10px 14px",background:ch.tagColor+"15",border:`1px solid ${ch.tagColor}40`,borderLeft:`4px solid ${ch.tagColor}`}}>
                        <div style={{fontSize:7,color:ch.tagColor,letterSpacing:2,marginBottom:4}}>{ch.tag}</div>
                        <div style={{fontSize:11,color:C.grayLight,lineHeight:1.6}}>{ch.note}</div>
                      </div>
                      <div style={{background:C.card,border:`1px solid ${C.border}`,padding:16}}>
                        <div style={{fontSize:8,color:C.gray,letterSpacing:2,marginBottom:12}}>CHALLENGE PARAMETERS</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {[
                            {label:"Profit Target",value:ch.target},
                            {label:"Max Drawdown",value:ch.maxDD,warn:true},
                            {label:"Daily Drawdown",value:ch.dailyDD,warn:true},
                            {label:"Min Trading Days",value:ch.minDays},
                            {label:"Leverage",value:ch.leverage},
                            {label:"Profit Split",value:ch.split},
                            {label:"EAs Allowed",value:ch.easAllowed?"✓ YES":"✗ NO",good:ch.easAllowed},
                            {label:"News Trading",value:ch.newsTrading?"✓ YES":"✗ NO",good:ch.newsTrading},
                            {label:"Weekend Holding",value:ch.weekendHold?"✓ YES":"✗ NO",good:ch.weekendHold},
                            {label:"Trading Period",value:"Unlimited"},
                          ].map((row,ri)=>(
                            <div key={ri} style={{padding:"8px 10px",background:C.bg,border:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <span style={{fontSize:9,color:C.gray}}>{row.label}</span>
                              <span style={{fontSize:10,fontWeight:700,color:row.good===true?C.green:row.good===false?C.red:row.warn?C.orange:C.white}}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{background:C.card,border:`1px solid ${C.border}`,padding:16}}>
                        <div style={{fontSize:8,color:C.gray,letterSpacing:2,marginBottom:12}}>ACCOUNT SIZES & FEES (ALL REFUNDABLE ON FIRST PAYOUT)</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                          {Object.entries(ch.fees).map(([size,fee])=>(
                            <div key={size} style={{padding:"10px 8px",background:C.bg,border:`1px solid ${C.border}`,textAlign:"center"}}>
                              <div style={{fontSize:11,color:C.gold,fontWeight:700}}>${size}</div>
                              <div style={{fontSize:10,color:C.grayLight,marginTop:3}}>{fee}</div>
                              <div style={{fontSize:7,color:C.muted,marginTop:2}}>refundable</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {ngTab==="rules"&&(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{padding:"10px 14px",background:C.redFaint,border:`1px solid ${C.red}40`,marginBottom:6}}>
                  <div style={{fontSize:8,color:C.red,letterSpacing:2,marginBottom:4}}>⚠ KNOW THESE BEFORE YOU BUY THE CHALLENGE</div>
                  <div style={{fontSize:11,color:C.grayLight,lineHeight:1.6}}>Breaking any of these rules = failed challenge. No exceptions. No refunds on a breach.</div>
                </div>
                {ngRules.map((r,ri)=>(
                  <div key={ri} style={{background:C.card,border:`1px solid ${r.color}30`,borderLeft:`4px solid ${r.color}`,padding:"12px 16px"}}>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                      <span style={{fontSize:12,fontWeight:700,color:C.white}}>{r.rule}</span>
                      <span style={{fontSize:7,letterSpacing:1,padding:"2px 7px",background:r.color+"20",border:`1px solid ${r.color}50`,color:r.color}}>{r.severity}</span>
                    </div>
                    <div style={{fontSize:11,color:C.gray,lineHeight:1.5}}>{r.desc}</div>
                  </div>
                ))}
              </div>
            )}

            {ngTab==="scaling"&&(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{background:C.card,border:`1px solid ${C.border}`,padding:16}}>
                  <div style={{fontSize:8,color:C.gold,letterSpacing:2,marginBottom:14}}>SCALING PLAN — THE LONG-TERM INCOME MACHINE (UP TO $1M)</div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr>{["ELAPSED TIME","ACCOUNT BALANCE","DAILY LOSS LIMIT","MAX LOSS LIMIT"].map(h=>(
                          <th key={h} style={{padding:"8px 12px",fontSize:8,letterSpacing:1,color:C.gold,borderBottom:`1px solid ${C.border}`,textAlign:"left"}}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {scalingPlan.map((row,ri)=>(
                          <tr key={ri} style={{background:ri%2===0?C.bg:"transparent"}}>
                            <td style={{padding:"10px 12px",fontSize:10,color:ri===0?C.cyan:C.green,fontWeight:ri===0?700:400}}>{row.months}</td>
                            <td style={{padding:"10px 12px",fontSize:11,color:C.white,fontWeight:700}}>{row.balance}</td>
                            <td style={{padding:"10px 12px",fontSize:10,color:C.grayLight}}>{row.daily}</td>
                            <td style={{padding:"10px 12px",fontSize:10,color:C.grayLight}}>{row.max}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  {[
                    {label:"Scale-Up Requirement",value:"10% net profit over 4 months (avg 2.5%/month) + 1 payout processed",color:C.cyan},
                    {label:"Scale Increment",value:"25% of initial balance added every qualifying 4-month period",color:C.green},
                    {label:"Maximum Account",value:"$1,000,000 per trader — the ceiling for scaling",color:C.gold},
                    {label:"Profit Split on Scaled",value:"90% on newly scaled-up accounts — grows with you",color:C.gold},
                  ].map((item,i)=>(
                    <div key={i} style={{padding:12,background:C.card,border:`1px solid ${item.color}30`}}>
                      <div style={{fontSize:8,color:item.color,letterSpacing:1,marginBottom:6}}>{item.label}</div>
                      <div style={{fontSize:11,color:C.grayLight,lineHeight:1.5}}>{item.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{padding:14,background:C.goldFaint,border:`1px solid ${C.gold}40`}}>
                  <div style={{fontSize:8,color:C.gold,letterSpacing:2,marginBottom:8}}>THE MATH: WHAT CONSISTENT TRADING LOOKS LIKE</div>
                  {[
                    "Start: $100K challenge → pass → funded at $100K, challenge fee refunded",
                    "Months 1–4: Average 2.5%/month = $10K total profit → qualify for scale-up",
                    "Month 4: Account bumped to $125K. Profit split locked at 90%.",
                    "Month 8: $150K. Month 12: $175K. Month 16: $200K account.",
                    "At $200K with 2.5%/month: $5,000 profit × 90% = $4,500/month to you",
                    "Continue scaling every 4 months — path to $400K–$1M account is on the table",
                  ].map((line,li)=>(
                    <div key={li} style={{display:"flex",gap:8,marginBottom:5}}>
                      <span style={{color:C.gold,flexShrink:0}}>→</span>
                      <span style={{fontSize:10,color:C.grayLight,lineHeight:1.5}}>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ngTab==="strategy"&&(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{background:C.card,border:`1px solid ${C.green}30`,padding:16}}>
                  <div style={{fontSize:8,color:C.green,letterSpacing:2,marginBottom:14}}>THE FLEXX ACADEMY NEXTGEN CHALLENGE PASS SYSTEM</div>
                  {[
                    {phase:"PRE-CHALLENGE (Month 3, Week 11)",color:C.cyan,steps:[
                      "Set up a dedicated 'mock challenge' demo account with exact NextGen rules",
                      "Set a 4% daily DD personal alert (buffer before 5% kill switch) — non-negotiable",
                      "Only trade A+ and A setups — no B or C setups during a challenge",
                      "Pass the mock challenge twice on demo before buying the real one",
                      "Confirm your EA passes the mock with no rule breaches before deploying",
                    ]},
                    {phase:"PHASE 1 — 10% TARGET (Standard Challenge)",color:C.gold,steps:[
                      "Target 0.5–1% per day. Hit 10% in 10–20 trading days. No rushing.",
                      "Drop to 0.5% risk per trade during challenge — tighter than your normal 1%",
                      "With 1:2 R:R at 0.5% risk: 2 wins = 2% in a day. Very achievable.",
                      "5% daily DD at 0.5% risk = you can have 10 losing trades in one day before breach",
                      "Trade London or NY session only — peak liquidity means cleaner setups",
                      "No trading Monday open or Friday close — highest spread/manipulation windows",
                      "ForexFactory check EVERY day — avoid holding open trades through red-folder events",
                    ]},
                    {phase:"PHASE 2 — 5% TARGET (Standard Challenge)",color:C.green,steps:[
                      "The hardest part is NOT blowing it when you're already close to the target",
                      "Drop to 0.25% risk per trade — protect what you built in Phase 1",
                      "Only 5% needed = achievable in 5–10 clean trading days",
                      "Minimum 3 days requirement: spread trades across at least 3 calendar days",
                      "When 3% profit locked in — drop risk to 0.1% and 'protect the pass'",
                      "Deploy EA if tested and proven — let it run your system around the clock",
                    ]},
                    {phase:"FUNDED ACCOUNT — PROTECT AND SCALE",color:C.gold,steps:[
                      "Back to standard 1% risk — you're now trading real-mirrored capital",
                      "First payout request after $500+ profit — your challenge fee is returned",
                      "Target 2.5% per month average for 4 months to qualify for scale-up",
                      "Track: monthly P&L, payout history, scale eligibility — in a spreadsheet",
                      "Treat this account like your career — not a lottery ticket. Protect it.",
                    ]},
                  ].map((section,si)=>(
                    <div key={si} style={{marginBottom:16,paddingBottom:16,borderBottom:si<3?`1px solid ${C.border}`:"none"}}>
                      <div style={{fontSize:8,color:section.color,letterSpacing:2,marginBottom:8,padding:"4px 10px",background:section.color+"15",border:`1px solid ${section.color}30`,display:"inline-block"}}>{section.phase}</div>
                      {section.steps.map((step,sti)=>(
                        <div key={sti} style={{display:"flex",gap:10,marginBottom:5}}>
                          <span style={{color:section.color,flexShrink:0,fontSize:10}}>{String(sti+1).padStart(2,"0")}</span>
                          <span style={{fontSize:11,color:C.grayLight,lineHeight:1.5}}>{step}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════ PARETO SECTION ══════ */}
        {mainSection==="pareto"&&(
          <div>
            <div style={{fontSize:8,color:C.green,letterSpacing:3,marginBottom:12}}>◆ THE MASTER PARETO — 10 CONCEPTS THAT GIVE 80% OF ALL TRADING RESULTS</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {pareto.map((p,pi)=>(
                <div key={pi} style={{display:"flex",gap:12,padding:"10px 14px",background:C.card,border:`1px solid ${C.border}`,alignItems:"flex-start"}}>
                  <div style={{minWidth:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",background:C.goldFaint,border:`1px solid ${C.gold}50`,fontSize:10,color:C.gold,fontWeight:700,flexShrink:0}}>{p.rank}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:C.white,fontWeight:600,marginBottom:2}}>{p.concept}</div>
                    <div style={{fontSize:10,color:C.gray,lineHeight:1.5}}>{p.impact}</div>
                  </div>
                  <div style={{fontSize:7,letterSpacing:1,padding:"3px 7px",background:C.border,color:C.gray,flexShrink:0,alignSelf:"center"}}>{p.market}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════ RESOURCES SECTION ══════ */}
        {mainSection==="resources"&&(
          <div>
            <div style={{fontSize:8,color:C.purple,letterSpacing:3,marginBottom:12}}>◆ BEST LEARNING RESOURCES BY CATEGORY</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {resources.map((cat,ci)=>(
                <div key={ci} style={{background:C.card,border:`1px solid ${cat.color}30`,padding:14}}>
                  <div style={{fontSize:8,color:cat.color,letterSpacing:2,marginBottom:10}}>{cat.category}</div>
                  {cat.items.map((item,ii)=>(
                    <div key={ii} style={{padding:"7px 0",borderBottom:ii<cat.items.length-1?`1px solid ${C.border}`:"none"}}>
                      <div style={{display:"flex",justifyContent:"space-between",gap:6,marginBottom:2}}>
                        <span style={{fontSize:10,color:C.white,fontWeight:600}}>{item.name}</span>
                        <span style={{fontSize:7,color:cat.color,letterSpacing:1,flexShrink:0}}>{item.type}</span>
                      </div>
                      <div style={{fontSize:9,color:C.gray,lineHeight:1.4}}>{item.note}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div style={{marginTop:24,padding:"14px 0",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:8,color:C.muted,letterSpacing:2}}>FLEXX ACADEMY · TRADING ROADMAP 2026</div>
          <div style={{fontSize:8,color:C.muted,letterSpacing:2}}>FOR EDUCATIONAL PURPOSES · NOT FINANCIAL ADVICE</div>
        </div>
      </div>
    </div>
  );
}
